import React from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Button,
  Image,
  SimpleGrid,
  Flex,
  Icon,
  Badge,
  Divider,
  useColorModeValue,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Stack,
  HStack,
  List,
  ListItem,
  ListIcon,
} from "@chakra-ui/react";
import { ChevronRightIcon, CheckIcon, StarIcon } from "@chakra-ui/icons";
import { FaServer, FaShieldAlt, FaUsers, FaCalendarAlt, FaShoppingCart, FaFileInvoiceDollar, FaTasks, FaBitcoin, FaLink, FaCode, FaLayerGroup, FaHandshake, FaMobileAlt, FaEnvelope, FaBullhorn, FaVideo, FaCoins } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { NavbarWithCallToAction } from "../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../components/chakra/FooterWithFourColumns/FooterWithFourColumns";

const Services = () => {
  const navigate = useNavigate();
  const bg = useColorModeValue("#EFEDDC", "gray.800");
  const cardBg = useColorModeValue("white", "gray.700");
  const highlightColor = useColorModeValue("green.600", "green.300");
  const accentColor = useColorModeValue("blue.600", "blue.300");
  
  const handleScheduleConsultation = () => {
    navigate("/sessions/practitioner-calendar/67f32cb6e7217642b7a9194d");
  };

  return (
    <Box bg={bg} minHeight="100vh">
      <NavbarWithCallToAction />

      <Container maxW="1200px" py={12}>
        <VStack spacing={12} align="stretch">
          {/* Hero Section */}
          <Box 
            textAlign="center" 
            py={10} 
            px={6} 
            borderRadius="lg" 
            bg="blue.50" 
            boxShadow="md"
            position="relative"
            overflow="hidden"
            _before={{
              content: "\"\"",
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "5px",
              background: "linear-gradient(90deg, #335786, #2C7A7B)",
              zIndex: 0
            }}
          >
            <Heading as="h1" size="2xl" mb={4} color="blue.700">
              Business & Blockchain Solutions
            </Heading>
            <Text fontSize="xl" mb={6} color="gray.600" maxW="800px" mx="auto">
              With a decade of blockchain development experience and a pending PhD in Blockchain Security,
              we specialize in creating secure, scalable solutions for modern businesses.
            </Text>
            <Badge colorScheme="green" fontSize="md" px={4} py={1} mb={4}>
              Complete Business Suite
            </Badge>
            <Divider my={6} maxW="200px" mx="auto" borderColor="blue.200" />
            <Text fontSize="lg" fontWeight="bold" color="blue.700">
              Life Essential Services: 
              Making your business sovereign, collaborative, and worthy of trust in a digital world.
            </Text>
          </Box>

          {/* Web Development Section */}
          <Card 
            bg={cardBg} 
            boxShadow="lg" 
            borderRadius="xl" 
            borderWidth="1px" 
            borderColor="blue.100"
            overflow="hidden"
            transition="all 0.3s ease"
            _hover={{
              transform: "translateY(-5px)",
              boxShadow: "xl"
            }}
          >
            <CardHeader bg="blue.50" borderBottomWidth="1px" borderColor="blue.100">
              <Flex align="center">
                <Icon as={FaCode} w={7} h={7} color="blue.500" mr={4} />
                <Heading size="lg" color="blue.700">Custom Website Development</Heading>
              </Flex>
            </CardHeader>
            <CardBody p={8}>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
                <Box>
                  <Text mb={4} fontSize="lg">
                    Modern, responsive websites built with the latest technologies. We create custom solutions tailored to your business needs, ensuring optimal performance and user experience.
                  </Text>
                  <List spacing={3} mt={4}>
                    <ListItem>
                      <ListIcon as={CheckIcon} color="green.500" />
                      React-based frontend with beautiful UI components
                    </ListItem>
                    <ListItem>
                      <ListIcon as={CheckIcon} color="green.500" />
                      GraphQL API integration for flexible data handling
                    </ListItem>
                    <ListItem>
                      <ListIcon as={CheckIcon} color="green.500" />
                      Mobile-responsive design for all screen sizes
                    </ListItem>
                    <ListItem>
                      <ListIcon as={CheckIcon} color="green.500" />
                      Fast deployment with continuous updates
                    </ListItem>
                  </List>
                  <Button
                    mt={6}
                    colorScheme="blue"
                    rightIcon={<ChevronRightIcon />}
                    onClick={handleScheduleConsultation}
                  >
                    Get Started
                  </Button>
                </Box>
                <Flex justify="center" align="center">
                  <Box 
                    bg="blue.50" 
                    p={4} 
                    borderRadius="xl" 
                    width="100%" 
                    maxW="300px"
                    height="250px"
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    textAlign="center"
                  >
                    <Icon as={FaLayerGroup} w={20} h={20} color="blue.500" mb={4} />
                    <Text fontWeight="bold" color="blue.700">Modern Web Stack</Text>
                    <Text fontSize="sm" color="blue.600">React • GraphQL • Node.js</Text>
                  </Box>
                </Flex>
              </SimpleGrid>
            </CardBody>
          </Card>

          {/* Blockchain Solutions Section */}
          <Card 
            bg={cardBg} 
            boxShadow="lg" 
            borderRadius="xl" 
            borderWidth="1px" 
            borderColor="green.100"
            overflow="hidden"
            transition="all 0.3s ease"
            _hover={{
              transform: "translateY(-5px)",
              boxShadow: "xl"
            }}
          >
            <CardHeader bg="green.50" borderBottomWidth="1px" borderColor="green.100">
              <Flex align="center">
                <Icon as={FaShieldAlt} w={7} h={7} color="green.500" mr={4} />
                <Heading size="lg" color="green.700">Blockchain Security & Integration</Heading>
              </Flex>
            </CardHeader>
            <CardBody p={8}>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
                <Box>
                  <Text mb={4} fontSize="lg">
                    Integrate blockchain technology into your business with our expert solutions. From secure crypto payments to transparent supply chains, we'll enhance your operations with distributed ledger technology.
                  </Text>
                  <List spacing={3} mt={4}>
                    <ListItem>
                      <ListIcon as={CheckIcon} color="green.500" />
                      Crypto wallet integration for secure transactions
                    </ListItem>
                    <ListItem>
                      <ListIcon as={CheckIcon} color="green.500" />
                      Custom token development and deployment
                    </ListItem>
                    <ListItem>
                      <ListIcon as={CheckIcon} color="green.500" />
                      Web3 implementation for decentralized applications
                    </ListItem>
                    <ListItem>
                      <ListIcon as={CheckIcon} color="green.500" />
                      Hardware wallet setup and security training
                    </ListItem>
                  </List>
                  <Button
                    mt={6}
                    colorScheme="green"
                    rightIcon={<ChevronRightIcon />}
                    onClick={handleScheduleConsultation}
                  >
                    Secure Your Business
                  </Button>
                </Box>
                <Flex justify="center" align="center">
                  <Box 
                    bg="green.50" 
                    p={4} 
                    borderRadius="xl" 
                    width="100%" 
                    maxW="300px"
                    height="250px"
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    textAlign="center"
                  >
                    <Icon as={FaBitcoin} w={20} h={20} color="green.500" mb={4} />
                    <Text fontWeight="bold" color="green.700">Blockchain Expertise</Text>
                    <Text fontSize="sm" color="green.600">Smart Contracts • Tokenization • Security</Text>
                  </Box>
                </Flex>
              </SimpleGrid>
            </CardBody>
          </Card>

          {/* Business Module Suite Section */}
          <Box my={8}>
            <Heading as="h2" size="xl" mb={8} textAlign="center" color={highlightColor}>
              Business Module Suite
            </Heading>
            <Text fontSize="lg" textAlign="center" mb={10} maxW="800px" mx="auto">
              Our comprehensive business suite offers modular solutions that can be implemented individually or combined for a powerful, integrated platform. Each module is designed to enhance your business operations.
            </Text>

            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8}>
              {/* Client Management Module */}
              <Card 
                bg={cardBg} 
                height="100%"
                boxShadow="md" 
                borderRadius="xl" 
                borderWidth="1px" 
                borderColor="gray.200"
                overflow="hidden"
                transition="all 0.3s ease"
                _hover={{
                  transform: "translateY(-5px)",
                  boxShadow: "xl"
                }}
                position="relative"
              >
                <CardHeader bg="purple.50" py={4}>
                  <Flex align="center">
                    <Icon as={FaUsers} w={6} h={6} color="purple.500" mr={3} />
                    <Heading size="md" color="purple.700">Client Management</Heading>
                  </Flex>
                </CardHeader>
                <CardBody p={5} position="relative">
                  <Badge 
                    position="absolute" 
                    top={0} 
                    left={0} 
                    colorScheme="purple" 
                    fontSize="xs" 
                    px={2} 
                    py={0.5} 
                    borderRadius="md"
                    transform="translateY(-50%)"
                    boxShadow="sm"
                  >
                    Blockchain & AI Compatible
                  </Badge>
                  <Text mb={4}>
                    A complete client management system with profiles, communication history, and document storage. Track client interactions and maintain detailed records.
                  </Text>
                  <List spacing={2} mb={4}>
                    <ListItem fontSize="sm">
                      <ListIcon as={CheckIcon} color="green.500" />
                      Client profiles and contact management
                    </ListItem>
                    <ListItem fontSize="sm">
                      <ListIcon as={CheckIcon} color="green.500" />
                      Secure document storage
                    </ListItem>
                    <ListItem fontSize="sm">
                      <ListIcon as={CheckIcon} color="green.500" />
                      Communication tracking
                    </ListItem>
                  </List>
                </CardBody>
                <CardFooter bg="gray.50" py={3}>
                  <Button 
                    colorScheme="purple" 
                    size="sm" 
                    width="100%"
                    variant="outline"
                    onClick={handleScheduleConsultation}
                  >
                    Get Started
                  </Button>
                </CardFooter>
              </Card>

              {/* Booking Calendar Module */}
              <Card 
                bg={cardBg} 
                height="100%"
                boxShadow="md" 
                borderRadius="xl" 
                borderWidth="1px" 
                borderColor="gray.200"
                overflow="hidden"
                transition="all 0.3s ease"
                _hover={{
                  transform: "translateY(-5px)",
                  boxShadow: "xl"
                }}
                position="relative"
              >
                <CardHeader bg="blue.50" py={4}>
                  <Flex align="center">
                    <Icon as={FaCalendarAlt} w={6} h={6} color="blue.500" mr={3} />
                    <Heading size="md" color="blue.700">Booking Calendar</Heading>
                  </Flex>
                </CardHeader>
                <CardBody p={5} position="relative">
                  <Badge 
                    position="absolute" 
                    top={0} 
                    left={0} 
                    colorScheme="blue" 
                    fontSize="xs" 
                    px={2} 
                    py={0.5} 
                    borderRadius="md"
                    transform="translateY(-50%)"
                    boxShadow="sm"
                  >
                    Blockchain & AI Compatible
                  </Badge>
                  <Text mb={4}>
                    A sophisticated booking system with availability management, session types, and automated reminders. Perfect for scheduling consultations, events, or services.
                  </Text>
                  <List spacing={2} mb={4}>
                    <ListItem fontSize="sm">
                      <ListIcon as={CheckIcon} color="green.500" />
                      Customizable session types
                    </ListItem>
                    <ListItem fontSize="sm">
                      <ListIcon as={CheckIcon} color="green.500" />
                      SMS & email reminders
                    </ListItem>
                    <ListItem fontSize="sm">
                      <ListIcon as={CheckIcon} color="green.500" />
                      Integrated payment processing
                    </ListItem>
                  </List>
                </CardBody>
                <CardFooter bg="gray.50" py={3}>
                  <Button 
                    colorScheme="blue" 
                    size="sm" 
                    width="100%"
                    variant="outline"
                    onClick={handleScheduleConsultation}
                  >
                    Get Started
                  </Button>
                </CardFooter>
              </Card>

              {/* Product Marketplace Module */}
              <Card 
                bg={cardBg} 
                height="100%"
                boxShadow="md" 
                borderRadius="xl" 
                borderWidth="1px" 
                borderColor="gray.200"
                overflow="hidden"
                transition="all 0.3s ease"
                _hover={{
                  transform: "translateY(-5px)",
                  boxShadow: "xl"
                }}
                position="relative"
              >
                <CardHeader bg="orange.50" py={4}>
                  <Flex align="center">
                    <Icon as={FaShoppingCart} w={6} h={6} color="orange.500" mr={3} />
                    <Heading size="md" color="orange.700">Product Marketplace</Heading>
                  </Flex>
                </CardHeader>
                <CardBody p={5} position="relative">
                  <Badge 
                    position="absolute" 
                    top={0} 
                    left={0} 
                    colorScheme="orange" 
                    fontSize="xs" 
                    px={2} 
                    py={0.5} 
                    borderRadius="md"
                    transform="translateY(-50%)"
                    boxShadow="sm"
                  >
                    Blockchain & AI Compatible
                  </Badge>
                  <Text mb={4}>
                    A complete e-commerce solution for listing and selling products. Includes product management, cart functionality, and order processing.
                  </Text>
                  <List spacing={2} mb={4}>
                    <ListItem fontSize="sm">
                      <ListIcon as={CheckIcon} color="green.500" />
                      Product listings with images
                    </ListItem>
                    <ListItem fontSize="sm">
                      <ListIcon as={CheckIcon} color="green.500" />
                      Shopping cart functionality
                    </ListItem>
                    <ListItem fontSize="sm">
                      <ListIcon as={CheckIcon} color="green.500" />
                      Order management
                    </ListItem>
                  </List>
                </CardBody>
                <CardFooter bg="gray.50" py={3}>
                  <Button 
                    colorScheme="orange" 
                    size="sm" 
                    width="100%"
                    variant="outline"
                    onClick={handleScheduleConsultation}
                  >
                    Get Started
                  </Button>
                </CardFooter>
              </Card>

              {/* Billing System Module */}
              <Card 
                bg={cardBg} 
                height="100%"
                boxShadow="md" 
                borderRadius="xl" 
                borderWidth="1px" 
                borderColor="gray.200"
                overflow="hidden"
                transition="all 0.3s ease"
                _hover={{
                  transform: "translateY(-5px)",
                  boxShadow: "xl"
                }}
                position="relative"
              >
                <CardHeader bg="green.50" py={4}>
                  <Flex align="center">
                    <Icon as={FaFileInvoiceDollar} w={6} h={6} color="green.500" mr={3} />
                    <Heading size="md" color="green.700">Billing System</Heading>
                  </Flex>
                </CardHeader>
                <CardBody p={5} position="relative">
                  <Badge 
                    position="absolute" 
                    top={0} 
                    left={0} 
                    colorScheme="green" 
                    fontSize="xs" 
                    px={2} 
                    py={0.5} 
                    borderRadius="md"
                    transform="translateY(-50%)"
                    boxShadow="sm"
                  >
                    Blockchain & AI Compatible
                  </Badge>
                  <Text mb={4}>
                    A configurable billing system that integrates with accounting software. Create and manage invoices, track payments, and generate financial reports.
                  </Text>
                  <List spacing={2} mb={4}>
                    <ListItem fontSize="sm">
                      <ListIcon as={CheckIcon} color="green.500" />
                      Invoice generation
                    </ListItem>
                    <ListItem fontSize="sm">
                      <ListIcon as={CheckIcon} color="green.500" />
                      Payment tracking
                    </ListItem>
                    <ListItem fontSize="sm">
                      <ListIcon as={CheckIcon} color="green.500" />
                      Accounting software integration
                    </ListItem>
                  </List>
                </CardBody>
                <CardFooter bg="gray.50" py={3}>
                  <Button 
                    colorScheme="green" 
                    size="sm" 
                    width="100%"
                    variant="outline"
                    onClick={handleScheduleConsultation}
                  >
                    Get Started
                  </Button>
                </CardFooter>
              </Card>

              {/* Project Management Module */}
              <Card 
                bg={cardBg} 
                height="100%"
                boxShadow="md" 
                borderRadius="xl" 
                borderWidth="1px" 
                borderColor="gray.200"
                overflow="hidden"
                transition="all 0.3s ease"
                _hover={{
                  transform: "translateY(-5px)",
                  boxShadow: "xl"
                }}
                position="relative"
              >
                <CardHeader bg="teal.50" py={4}>
                  <Flex align="center">
                    <Icon as={FaTasks} w={6} h={6} color="teal.500" mr={3} />
                    <Heading size="md" color="teal.700">Project Management</Heading>
                  </Flex>
                </CardHeader>
                <CardBody p={5} position="relative">
                  <Badge 
                    position="absolute" 
                    top={0} 
                    left={0} 
                    colorScheme="teal" 
                    fontSize="xs" 
                    px={2} 
                    py={0.5} 
                    borderRadius="md"
                    transform="translateY(-50%)"
                    boxShadow="sm"
                  >
                    Blockchain & AI Compatible
                  </Badge>
                  <Text mb={4}>
                    A client-facing project management system to track progress, manage tasks, and share updates. Upload evidence and maintain transparent communication.
                  </Text>
                  <List spacing={2} mb={4}>
                    <ListItem fontSize="sm">
                      <ListIcon as={CheckIcon} color="green.500" />
                      Task management
                    </ListItem>
                    <ListItem fontSize="sm">
                      <ListIcon as={CheckIcon} color="green.500" />
                      Progress tracking
                    </ListItem>
                    <ListItem fontSize="sm">
                      <ListIcon as={CheckIcon} color="green.500" />
                      File uploads and evidence
                    </ListItem>
                  </List>
                </CardBody>
                <CardFooter bg="gray.50" py={3}>
                  <Button 
                    colorScheme="teal" 
                    size="sm" 
                    width="100%"
                    variant="outline"
                    onClick={handleScheduleConsultation}
                  >
                    Get Started
                  </Button>
                </CardFooter>
              </Card>

              {/* Supply Chain Tracking */}
              <Card 
                bg={cardBg} 
                height="100%"
                boxShadow="md" 
                borderRadius="xl" 
                borderWidth="1px" 
                borderColor="gray.200"
                overflow="hidden"
                transition="all 0.3s ease"
                _hover={{
                  transform: "translateY(-5px)",
                  boxShadow: "xl"
                }}
                position="relative"
              >
                <CardHeader bg="cyan.50" py={4}>
                  <Flex align="center">
                    <Icon as={FaLink} w={6} h={6} color="cyan.500" mr={3} />
                    <Heading size="md" color="cyan.700">Supply Chain Tracking</Heading>
                  </Flex>
                </CardHeader>
                <CardBody p={5} position="relative">
                  <Badge 
                    position="absolute" 
                    top={0} 
                    left={0} 
                    colorScheme="cyan" 
                    fontSize="xs" 
                    px={2} 
                    py={0.5} 
                    borderRadius="md"
                    transform="translateY(-50%)"
                    boxShadow="sm"
                  >
                    Blockchain & AI Compatible
                  </Badge>
                  <Text mb={4}>
                    Blockchain-based supply chain tracking systems to enhance transparency, reduce fraud, and improve efficiency in your logistics operations.
                  </Text>
                  <List spacing={2} mb={4}>
                    <ListItem fontSize="sm">
                      <ListIcon as={CheckIcon} color="green.500" />
                      Immutable product tracking
                    </ListItem>
                    <ListItem fontSize="sm">
                      <ListIcon as={CheckIcon} color="green.500" />
                      Transparent supply chain
                    </ListItem>
                    <ListItem fontSize="sm">
                      <ListIcon as={CheckIcon} color="green.500" />
                      QR code integration
                    </ListItem>
                  </List>
                </CardBody>
                <CardFooter bg="gray.50" py={3}>
                  <Button 
                    colorScheme="cyan" 
                    size="sm" 
                    width="100%"
                    variant="outline"
                    onClick={handleScheduleConsultation}
                  >
                    Get Started
                  </Button>
                </CardFooter>
              </Card>
            </SimpleGrid>
          </Box>

          {/* Blockchain Specific Solutions */}
          <Box my={12}>
            <Heading as="h2" size="xl" mb={8} textAlign="center" color="blue.700">
              Advanced Blockchain Solutions
            </Heading>
            <Text fontSize="lg" textAlign="center" mb={10} maxW="800px" mx="auto">
              Leverage our expertise in blockchain technology to create secure, decentralized systems for your organization.
            </Text>

            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8}>
              {/* Secure Video Server */}
              <Card 
                bg={cardBg} 
                height="100%"
                boxShadow="md" 
                borderRadius="xl" 
                borderWidth="1px" 
                borderColor="blue.200"
                overflow="hidden"
                transition="all 0.3s ease"
                _hover={{
                  transform: "translateY(-5px)",
                  boxShadow: "xl"
                }}
              >
                <CardHeader bg="blue.50" py={4}>
                  <Flex align="center">
                    <Icon as={FaVideo} w={6} h={6} color="blue.500" mr={3} />
                    <Heading size="md" color="blue.700">Secure Video Server</Heading>
                  </Flex>
                </CardHeader>
                <CardBody p={5}>
                  <Text mb={4}>
                    End-to-end encrypted video conferencing platform with blockchain verification, ensuring complete privacy and security for your sensitive communications.
                  </Text>
                  <List spacing={2} mb={4}>
                    <ListItem fontSize="sm">
                      <ListIcon as={CheckIcon} color="green.500" />
                      End-to-end encryption
                    </ListItem>
                    <ListItem fontSize="sm">
                      <ListIcon as={CheckIcon} color="green.500" />
                      Self-hosted solution
                    </ListItem>
                    <ListItem fontSize="sm">
                      <ListIcon as={CheckIcon} color="green.500" />
                      Blockchain-verified identity
                    </ListItem>
                    <ListItem fontSize="sm">
                      <ListIcon as={CheckIcon} color="green.500" />
                      Screen sharing and recording
                    </ListItem>
                  </List>
                </CardBody>
                <CardFooter bg="gray.50" py={3}>
                  <Button 
                    colorScheme="blue" 
                    size="sm" 
                    width="100%"
                    variant="outline"
                    onClick={handleScheduleConsultation}
                  >
                    Secure Your Communications
                  </Button>
                </CardFooter>
              </Card>

              {/* Crypto Platform Deployment */}
              <Card 
                bg={cardBg} 
                height="100%"
                boxShadow="md" 
                borderRadius="xl" 
                borderWidth="1px" 
                borderColor="purple.200"
                overflow="hidden"
                transition="all 0.3s ease"
                _hover={{
                  transform: "translateY(-5px)",
                  boxShadow: "xl"
                }}
              >
                <CardHeader bg="purple.50" py={4}>
                  <Flex align="center">
                    <Icon as={FaCoins} w={6} h={6} color="purple.500" mr={3} />
                    <Heading size="md" color="purple.700">Crypto Platform Deployment</Heading>
                  </Flex>
                </CardHeader>
                <CardBody p={5}>
                  <Text mb={4}>
                    Custom token creation and cryptocurrency platform development. Deploy your own tokens, NFTs, or complete blockchain-based ecosystems.
                  </Text>
                  <List spacing={2} mb={4}>
                    <ListItem fontSize="sm">
                      <ListIcon as={CheckIcon} color="green.500" />
                      ERC-20/ERC-721 token creation
                    </ListItem>
                    <ListItem fontSize="sm">
                      <ListIcon as={CheckIcon} color="green.500" />
                      Smart contract development
                    </ListItem>
                    <ListItem fontSize="sm">
                      <ListIcon as={CheckIcon} color="green.500" />
                      Custom blockchain solutions
                    </ListItem>
                    <ListItem fontSize="sm">
                      <ListIcon as={CheckIcon} color="green.500" />
                      Multi-chain deployment
                    </ListItem>
                  </List>
                </CardBody>
                <CardFooter bg="gray.50" py={3}>
                  <Button 
                    colorScheme="purple" 
                    size="sm" 
                    width="100%"
                    variant="outline"
                    onClick={handleScheduleConsultation}
                  >
                    Deploy Your Platform
                  </Button>
                </CardFooter>
              </Card>

              {/* Governance Tools */}
              <Card 
                bg={cardBg} 
                height="100%"
                boxShadow="md" 
                borderRadius="xl" 
                borderWidth="1px" 
                borderColor="green.200"
                overflow="hidden"
                transition="all 0.3s ease"
                _hover={{
                  transform: "translateY(-5px)",
                  boxShadow: "xl"
                }}
              >
                <CardHeader bg="green.50" py={4}>
                  <Flex align="center">
                    <Icon as={FaHandshake} w={6} h={6} color="green.500" mr={3} />
                    <Heading size="md" color="green.700">DAO Governance Tools</Heading>
                  </Flex>
                </CardHeader>
                <CardBody p={5}>
                  <Text mb={4}>
                    Complete suite of decentralized organization tools for transparent decision-making, voting, and accountability in distributed teams and communities.
                  </Text>
                  <List spacing={2} mb={4}>
                    <ListItem fontSize="sm">
                      <ListIcon as={CheckIcon} color="green.500" />
                      Transparent proposal systems
                    </ListItem>
                    <ListItem fontSize="sm">
                      <ListIcon as={CheckIcon} color="green.500" />
                      Secure voting mechanisms
                    </ListItem>
                    <ListItem fontSize="sm">
                      <ListIcon as={CheckIcon} color="green.500" />
                      Token-based governance
                    </ListItem>
                    <ListItem fontSize="sm">
                      <ListIcon as={CheckIcon} color="green.500" />
                      Multi-sig treasury management
                    </ListItem>
                  </List>
                </CardBody>
                <CardFooter bg="gray.50" py={3}>
                  <Button 
                    colorScheme="green" 
                    size="sm" 
                    width="100%"
                    variant="outline"
                    onClick={handleScheduleConsultation}
                  >
                    Decentralize Your Organization
                  </Button>
                </CardFooter>
              </Card>
            </SimpleGrid>
          </Box>

          {/* Additional Services */}
          <Box 
            p={8} 
            bg="blue.50" 
            borderRadius="xl" 
            boxShadow="md"
            mt={10}
          >
            <Heading as="h2" size="lg" mb={6} textAlign="center" color="blue.700">
              Additional Business Services
            </Heading>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8}>
              <Box p={4}>
                <Flex align="center" mb={3}>
                  <Icon as={FaMobileAlt} color="blue.500" w={5} h={5} mr={3} />
                  <Heading size="sm">SMS Integration</Heading>
                </Flex>
                <Text fontSize="sm">
                  Automated SMS notifications and reminders for appointments, updates, and marketing campaigns.
                </Text>
              </Box>
              <Box p={4}>
                <Flex align="center" mb={3}>
                  <Icon as={FaEnvelope} color="blue.500" w={5} h={5} mr={3} />
                  <Heading size="sm">Email Systems</Heading>
                </Flex>
                <Text fontSize="sm">
                  Custom email servers and notification systems for reliable business communications.
                </Text>
              </Box>
              <Box p={4}>
                <Flex align="center" mb={3}>
                  <Icon as={FaBullhorn} color="blue.500" w={5} h={5} mr={3} />
                  <Heading size="sm">Sales Funnels</Heading>
                </Flex>
                <Text fontSize="sm">
                  Integrated marketing funnels with lead capture, nurturing, and conversion tracking.
                </Text>
              </Box>
              <Box p={4}>
                <Flex align="center" mb={3}>
                  <Icon as={FaHandshake} color="blue.500" w={5} h={5} mr={3} />
                  <Heading size="sm">Governance Tools</Heading>
                </Flex>
                <Text fontSize="sm">
                  Transparent organizational tools for decision-making, voting, and accountability.
                </Text>
              </Box>
              <Box p={4}>
                <Flex align="center" mb={3}>
                  <Icon as={FaServer} color="blue.500" w={5} h={5} mr={3} />
                  <Heading size="sm">Self-Hosting Solutions</Heading>
                </Flex>
                <Text fontSize="sm">
                  Secure, tenant-based self-hosting options for complete data sovereignty.
                </Text>
              </Box>
              <Box p={4}>
                <Flex align="center" mb={3}>
                  <Icon as={FaBitcoin} color="blue.500" w={5} h={5} mr={3} />
                  <Heading size="sm">Crypto Payments</Heading>
                </Flex>
                <Text fontSize="sm">
                  Integrate cryptocurrency payment options with flexible processing and conversion.
                </Text>
              </Box>
            </SimpleGrid>
          </Box>

          {/* Research Partnership */}
          <Box 
            p={8} 
            bg="gray.50" 
            borderRadius="xl" 
            boxShadow="md"
            display="flex"
            flexDirection={{ base: "column", md: "row" }}
            alignItems="center"
            justifyContent="space-between"
            gap={6}
          >
            <Box maxW={{ base: "100%", md: "60%" }}>
              <Heading as="h3" size="md" mb={4} color="gray.700">
                Academic Research Partnership
              </Heading>
              <Text mb={3}>
                Several of our supply chain projects have been published in collaboration with Queensland University of Technology, bringing academic rigor to our blockchain implementations.
              </Text>
              <Text fontStyle="italic" fontSize="sm" color="gray.600">
                Advancing blockchain technology through research and practical applications.
              </Text>
            </Box>
            <Box 
              bg="white" 
              p={4} 
              borderRadius="lg" 
              boxShadow="sm"
              width={{ base: "100%", md: "200px" }}
              height="100px"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Text fontWeight="bold" color="blue.700" fontSize="xl">QUT</Text>
            </Box>
          </Box>

          {/* About Tom Section */}
          <Box 
            p={{ base: 6, md: 10 }} 
            bg="blue.50" 
            borderRadius="xl" 
            boxShadow="md"
            position="relative"
            overflow="hidden"
            _before={{
              content: "\"\"",
              position: "absolute",
              bottom: 0,
              right: 0,
              width: "300px",
              height: "300px",
              background: "radial-gradient(circle, rgba(57, 161, 105, 0.1) 0%, transparent 70%)",
              zIndex: 0
            }}
          >
            <Flex 
              direction={{ base: "column", md: "row" }} 
              align={{ base: "center", md: "center" }} 
              justify="space-between"
              gap={{ base: 8, md: 10 }}
            >
              <Box maxW={{ base: "100%", md: "60%" }} zIndex={1}>
                <Heading as="h3" size="lg" mb={4} color="blue.700">
                  About Tom Miller
                </Heading>
                <Text mb={3} fontSize="md">
                  Tom is completing his PhD in Blockchain Security at Queensland University of Technology and is a published scholar in the field, bringing academic insights to practical business applications.
                </Text>
                <Text mb={3} fontSize="md">
                  With over a decade of experience in blockchain development, Tom has a unique vision for how technology can foster human connection rather than replace it. His approach to business emphasizes that we're all connected—through our networks, friends, institutions, and financial transactions.
                </Text>
                <Text fontSize="md" fontStyle="italic" color="blue.700" p={4} bg="blue.100" borderRadius="md" my={3}>
                  "Blockchain and decentralized systems allow us to build transparent, accountable businesses that enhance human dignity rather than suppressing it. We're creating tools for sovereignty and cooperation, not control."
                </Text>
                <Button 
                  as="a" 
                  href="https://eprints.qut.edu.au/view/person/Miller,_Thomas.html" 
                  size="sm"
                  target="_blank"
                  colorScheme="blue" 
                  variant="solid" 
                  rightIcon={<ChevronRightIcon />}
                  mt={3}
                  _hover={{
                    transform: "translateY(-2px)",
                    boxShadow: "md"
                  }}
                  transition="all 0.2s"
                >
                  View publications and background
                </Button>
              </Box>
              <Box 
                bg="white" 
                p={3} 
                borderRadius="xl" 
                boxShadow="lg"
                minWidth={{ base: "200px", md: "250px" }}
                height={{ base: "200px", md: "250px" }}
                flexShrink={0}
                alignSelf="center"
                border="4px solid"
                borderColor="blue.100"
                overflow="hidden"
                transition="all 0.3s ease"
                _hover={{
                  transform: "scale(1.03)",
                  boxShadow: "xl",
                  borderColor: "blue.200"
                }}
              >
                <Image 
                  src="/tomprofile.jpeg" 
                  alt="Tom Miller" 
                  borderRadius="lg" 
                  objectFit="cover"
                  width="100%"
                  height="100%"
                />
              </Box>
            </Flex>
          </Box>

          {/* Call-to-Action Section */}
          <Box 
            textAlign="center" 
            py={10} 
            px={6} 
            bg="blue.600" 
            borderRadius="xl" 
            boxShadow="lg"
            color="white"
          >
            <Heading as="h2" size="xl" mb={4}>
              Ready to Transform Your Business?
            </Heading>
            <Text fontSize="lg" mb={8} maxW="700px" mx="auto">
              Let's discuss how blockchain and web solutions can drive your business forward.
              Our modular approach allows you to start with what you need and expand as you grow.
            </Text>
            <Button 
              colorScheme="white" 
              size="lg" 
              rightIcon={<ChevronRightIcon />}
              onClick={handleScheduleConsultation}
              variant="outline"
              _hover={{
                bg: "white",
                color: "blue.600"
              }}
              borderWidth={2}
              px={10}
              py={7}
              fontSize="xl"
            >
              Schedule a Consultation
            </Button>
          </Box>
        </VStack>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default Services; 